import { ActionIcon, Alert, Button, Code, ColorInput, CopyButton, Group, Paper, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconCopy, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCalendarFeed,
  useCalendarFeedAction,
  useDeleteExternalCalendar,
  useExternalCalendars,
  useSaveExternalCalendar,
} from '@/api/calendarIntegrations';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';

/** Persönlicher ICS-Feed (API-06) und abonnierte Kalender (CAL-03). */
export function CalendarIntegrationSection() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const onError = useApiError();
  const { data: feed } = useCalendarFeed(ctx.workspaceId);
  const feedAction = useCalendarFeedAction(ctx.workspaceId);
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        {t('calendarIntegration.title')}
      </Title>
      <Stack>
        <div>
          <Text fw={500} size="sm">
            {t('calendarIntegration.feedTitle')}
          </Text>
          <Text size="sm" c="dimmed" mb="xs">
            {t('calendarIntegration.feedText')}
          </Text>
          {feedUrl && (
            <Alert color="green" mb="xs">
              <Group gap="xs" wrap="nowrap">
                <Code style={{ wordBreak: 'break-all' }}>{feedUrl}</Code>
                <CopyButton value={feedUrl}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? t('common.copied') : t('apiKeys.copy')}>
                      <ActionIcon variant="subtle" onClick={copy} aria-label={t('apiKeys.copy')}>
                        <IconCopy size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" mt={4}>
                {t('calendarIntegration.feedOnce')}
              </Text>
            </Alert>
          )}
          <Group>
            <Button variant="light" loading={feedAction.isPending} onClick={() => feedAction.mutate('create', { onSuccess: (r) => setFeedUrl(r?.url ?? null), onError })}>
              {feed?.active ? t('calendarIntegration.feedRenew') : t('calendarIntegration.feedCreate')}
            </Button>
            {feed?.active && (
              <Button variant="subtle" color="red" onClick={() => feedAction.mutate('delete', { onSuccess: () => setFeedUrl(null), onError })}>
                {t('calendarIntegration.feedDisable')}
              </Button>
            )}
          </Group>
        </div>
        <ExternalCalendars />
      </Stack>
    </Paper>
  );
}

export function ExternalCalendars() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const onError = useApiError();
  const { data: calendars = [] } = useExternalCalendars(ctx.workspaceId);
  const save = useSaveExternalCalendar(ctx.workspaceId);
  const remove = useDeleteExternalCalendar(ctx.workspaceId);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [color, setColor] = useState('#7048e8');
  return (
    <div>
      <Text fw={500} size="sm">
        {t('calendarIntegration.externalTitle')}
      </Text>
      <Text size="sm" c="dimmed" mb="xs">
        {t('calendarIntegration.externalText')}
      </Text>
      {calendars.length > 0 && (
        <Table mb="sm">
          <Table.Tbody>
            {calendars.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>
                  <Group gap="xs">
                    <span style={{ width: 10, height: 10, borderRadius: 5, background: c.color ?? '#7048e8', display: 'inline-block' }} />
                    {c.name}
                  </Group>
                  {c.lastError && (
                    <Text size="xs" c="red">
                      {c.lastError}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td ta="right">
                  <ActionIcon variant="subtle" color="red" aria-label={t('common.delete')} onClick={() => remove.mutate(c.id, { onError })}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Group align="flex-end" wrap="wrap">
        <TextInput label={t('calendarIntegration.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} w={180} />
        <TextInput
          label={t('calendarIntegration.url')}
          placeholder="https://…/basic.ics"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 240 }}
        />
        <ColorInput label={t('calendarIntegration.color')} value={color} onChange={setColor} w={130} />
        <Button
          disabled={!url.trim()}
          loading={save.isPending}
          onClick={() =>
            save.mutate(
              { name: name || undefined, url, color },
              {
                onSuccess: () => {
                  setName('');
                  setUrl('');
                },
                onError,
              },
            )
          }
        >
          {t('calendarIntegration.add')}
        </Button>
      </Group>
    </div>
  );
}
